'use client'

import { motion } from 'framer-motion'
import { 
  Footer,
  Navigation
} from '@/ui/design-system'
import { PageLayout } from '@/ui/layout'
import { Logo } from '@/ui/components'
import { PageMeta } from '@/components/PageMeta'

export default function TermsOfService() {
  // Navigation configuration
  const navigationItems = [
    {
      title: "Home",
      href: "/",
    },
  ]

  const navigationActions = {
    primary: { label: "Get started", href: "/signup" },
    secondary: { label: "Sign in", href: "/login" }
  }

  // Footer configuration
  const footerSections = [
    {
      title: "Product",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Sign Up", href: "/signup" },
      ]
    },
    {
      title: "Support",
      links: [
        { label: "Documentation", href: "/docs" },
        { label: "Contact Us", href: "/contact" },
        { label: "Report Issue", href: "/contact" },
      ]
    },
    {
      title: "Legal",
      links: [
        { label: "Terms of Service", href: "/terms" },
        { label: "Privacy Policy", href: "/privacy" },
      ]
    }
  ]

  return (
    <PageLayout showHeader={false}>
      <PageMeta title="Terms of Service" description="SitRepp.io terms of service and use" />
      <Navigation
        logo={<Logo to="/" size="md" />}
        items={navigationItems}
        actions={navigationActions}
      />

      <div className="min-h-screen bg-earth-50">
        {/* Hero Section */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center max-w-4xl mx-auto mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
                Terms of Service
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-16">
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">1. Introduction</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Welcome to SitRepp.io ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our website, mobile application, and services (collectively, the "Service") for planning and coordinating overlanding trips.
                  </p>
                  <p>
                    By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access the Service.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">2. Acceptance of Terms</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    By creating an account, accessing, or using SitRepp.io, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not use the Service.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">3. Use License</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Permission is granted to temporarily download one copy of the materials on SitRepp.io for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Modify or copy the materials</li>
                    <li>Use the materials for any commercial purpose or for any public display</li>
                    <li>Attempt to reverse engineer any software contained on SitRepp.io</li>
                    <li>Remove any copyright or other proprietary notations from the materials</li>
                    <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                  </ul>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">4. User Accounts</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    To access certain features of the Service, you must register for an account. You agree to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide accurate, current, and complete information during registration</li>
                    <li>Maintain and promptly update your account information</li>
                    <li>Maintain the security of your password and identification</li>
                    <li>Accept all responsibility for activities that occur under your account</li>
                    <li>Notify us immediately of any unauthorized use of your account</li>
                  </ul>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">5. User Content</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    You retain ownership of any content you submit, post, or display on or through the Service ("User Content"). By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, and distribute your User Content solely for the purpose of operating and providing the Service.
                  </p>
                  <p>
                    You are responsible for your User Content and represent that you have all rights necessary to grant us the license above. You agree not to post User Content that:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Violates any third-party right, including copyright, trademark, privacy, or other personal or proprietary rights</li>
                    <li>Is illegal, harmful, threatening, abusive, or otherwise objectionable</li>
                    <li>Contains false or misleading information</li>
                    <li>Contains viruses, malware, or other harmful code</li>
                  </ul>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">6. Intellectual Property</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    The Service and its original content, features, and functionality are and will remain the exclusive property of SitRepp.io and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">7. Privacy Policy</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy to understand how we collect and use your information.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">8. Limitation of Liability</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    <strong className="text-foreground">IMPORTANT: OUTDOOR ACTIVITIES DISCLAIMER</strong>
                  </p>
                  <p>
                    Overlanding and outdoor activities involve inherent risks. You acknowledge and agree that:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>SitRepp.io is a trip planning and coordination tool only and does not guarantee the safety, accuracy, or suitability of any routes, locations, or trip information</li>
                    <li>You are solely responsible for assessing the risks of any trip and making your own safety decisions</li>
                    <li>We are not responsible for any injuries, damages, or losses that occur during or as a result of trips planned using our Service</li>
                    <li>You should always check local conditions, weather, regulations, and road closures before traveling</li>
                    <li>You should have appropriate equipment, training, and insurance for your activities</li>
                  </ul>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL SITREPP.IO, ITS DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">9. Termination</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will immediately cease.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">10. Changes to Terms</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">11. Governing Law</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved in the courts of competent jurisdiction.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">12. Contact Information</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    If you have any questions about these Terms, please contact us at:
                  </p>
                  <p>
                    <strong className="text-foreground">Email:</strong> <a href="mailto:support@sitrepp.io" className="text-primary hover:underline">support@sitrepp.io</a>
                  </p>
                  <p>
                    <strong className="text-foreground">Website:</strong> <a href="/contact" className="text-primary hover:underline">sitrepp.io/contact</a>
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">13. Effective Date</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    These Terms are effective as of {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} and will remain in effect except with respect to any changes in its provisions in the future, which will be in effect immediately after being posted on this page.
                  </p>
                </div>
              </motion.section>
            </div>
          </div>
        </section>
      </div>

      <Footer
        sections={footerSections}
        description="A collaborative workspace for overlanding crews. Coordinate trips, manage gear, plan meals, and share memories—all in one place."
      />
    </PageLayout>
  )
}

